export interface ComponentSearchItem {
  key: string;
  name: string;
  properties: string[];
  displayLabel: string;
}

/**
 * Returns the first property whose words all start with the supplied search terms.
 * Returns null when searchTerm is empty or no property matches.
 */
export function findMatchingProperty(component: ComponentSearchItem, searchTerm: string): string | null {
  if (!searchTerm) return null;
  const terms = searchTerm.toLowerCase().split(' ').filter(t => t.trim() !== '');
  const matched = component.properties?.find(prop => {
    const words = prop.toLowerCase().split(' ').filter(w => w.trim() !== '');
    return terms.every(term => words.some(word => word.startsWith(term)));
  });
  return matched ?? null;
}

/**
 * Returns the component name when it contains the search term (case-insensitive).
 * Returns the name unchanged when searchTerm is empty. Returns null for no match.
 */
export function matchesComponentName(component: ComponentSearchItem, searchTerm: string): string | null {
  if (!searchTerm) return component.name;
  return component.name.toLowerCase().includes(searchTerm.toLowerCase().trim()) ? component.name : null;
}

/**
 * Returns a filtered shallow copy of the components array.
 * Includes items that match by name or by property. Returns all items when searchTerm is empty.
 */
export function applyComponentFilter(components: ComponentSearchItem[], searchTerm: string): ComponentSearchItem[] {
  if (!searchTerm) return [...components];
  return components.filter(c =>
    findMatchingProperty(c, searchTerm) !== null || matchesComponentName(c, searchTerm) !== null
  );
}

/**
 * Returns the property value if the component contains the given matched property; undefined otherwise.
 */
export function componentHasMatchedProperty(component: ComponentSearchItem, matchedProperty: string | null): string | undefined {
  if (!matchedProperty) return undefined;
  return component.properties.find(x => x === matchedProperty);
}
